import { StyledComponent } from '@emotion/styled';
import { TextFieldProps } from '@mui/material';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WhiteTextField } from '../WhiteTextField';

const sanitizeTelephoneNumberChar = /[^0-9a-z+]/gi;
export const sanitizeTelephoneNumber = (telephone: string) => telephone.replace(sanitizeTelephoneNumberChar, '');

const cognitoPhoneNoRegExp = /^\+[1-9][0-9]{0,17}$/; // regex based on cognito OpenId Connect phone_number attribute. Updated to 17 digits limit - tested by experiment
export const isValidCognitoNumber = (telephone: string) => cognitoPhoneNoRegExp.test(telephone);

/* istanbul ignore next */
export function TelephoneInput({
  Component = WhiteTextField,
  value = '',
  onChange,
  placeholder = 'E.g. +44123456789',
  label = 'Telephone number',
  name = 'telephone',
  type = 'tel',
  error,
  ...props
}: Omit<TextFieldProps, 'variant'> & {
  Component: StyledComponent<TextFieldProps>;
}) {
  const [innerValue, setInnerValue] = useState(`${value}`);
  const inputRef = useRef<HTMLInputElement>(null);
  const [cursor, setCursor] = useState<number | null>(0);

  useEffect(() => {
    const input = inputRef.current;
    if (input) input.setSelectionRange(cursor, cursor);
  }, [inputRef, cursor, value]);

  const sanitized = sanitizeTelephoneNumber(innerValue);
  const valid =
    !innerValue || // show placeholder when empty
    isValidCognitoNumber(sanitized);

  const { t } = useTranslation('orders');

  return (
    <Component
      {...props}
      placeholder={placeholder}
      label={label}
      type={type}
      name={name}
      inputRef={inputRef}
      variant={Component.defaultProps?.variant || /* istanbul ignore next */ 'filled'}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        setCursor(inputRef.current!.selectionStart);
        const newValue = event.target.value;
        setInnerValue(newValue); // Keep internal state exactly as typed
        const newSanitized = sanitizeTelephoneNumber(newValue);
        const newValid = isValidCognitoNumber(newSanitized); // Validate sanitized form
        // Only set value when valid, and sanitize it before setting
        event.target.value = newValid ? newSanitized : /* istanbul ignore next */ ''; // Mutating for brevity. If not valid unset in parent.
        if (onChange) onChange(event);
      }}
      value={innerValue}
      error={
        !valid || error // Handle internal validation and required error from the top component
      }
      helperText={!valid && t('details.addPhoneNumberCountryCode')}
    />
  );
}
